import {
  ReactElement,
  useState,
  useMemo,
  useEffect,
} from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  HiOutlinePlayCircle,
} from 'react-icons/hi2';
import Button from '../../components/Button';
import { numberToLetters } from '../../utils/letterNumber';
import {
  DISC_LETTER_NUMBER,
} from '../../constants/apiConstants';
import {
  useSetWheelAndReprocessDynamic,
} from '../../hooks/useSessions';
import { useRollingRadiiElements } from '../../hooks/useEquipment';
import {
  RollingRadiiElement,
  RollingRadiiTyre,
} from '../../api/equipment';

const PARAM_FIELDS = [
  'wheel_circumference',
  'cda_ref',
  'cp_corr',
  'yaw_offset',
] as const;

const PARAM_HEADERS = [
  'Wheel Circ.',
  'CdA Ref',
  'Cp Corr',
  'Yaw Offset',
] as const;

interface ParametersCoordinationProps {
  sessions: any[];
  selectedSessionId: string;
}

function getValidConfigs(session: any): any[] {
  if (!session?.configs) return [];
  return session.configs
    .filter(
      (c: any) =>
        !c.isDeleted &&
        c.letter_number !== undefined &&
        c.letter_number < DISC_LETTER_NUMBER,
    )
    .sort(
      (a: any, b: any) =>
        (a.letter_number ?? 0) - (b.letter_number ?? 0),
    );
}

function formatVal(v: any): string {
  if (v == null) return '-';
  const n = parseFloat(String(v));
  if (isNaN(n)) return '-';
  return n.toPrecision(5);
}

function sessionLabel(session: any): string {
  const date =
    session.date ||
    (session.created_at
      ? new Date(session.created_at).toLocaleDateString()
      : '');
  const desc =
    session.description || session.name || 'Unnamed';
  return `${date} – ${desc}`;
}

interface TyreCatalogRow {
  kind: 'data' | 'average';
  tyreLabel: string;
  innerWidth: number;
  pressure: number;
  sessionDesc: string;
  configLetter: string;
  configDesc: string;
  runNumber: number | string;
  circumferenceCalc: number | null;
  nSamples?: number;
}

function buildTyreCatalogRows(
  sessions: any[],
  catalog: RollingRadiiTyre[] | undefined,
): TyreCatalogRow[] {
  if (!catalog || catalog.length === 0) return [];

  // Index catalog elements by run.id for O(1) joins, and remember which
  // tyre each element belongs to for name/manufacturer lookup.
  const elementByRunId = new Map<
    string,
    { element: RollingRadiiElement; tyre: RollingRadiiTyre }
  >();
  const tyreById = new Map<string, RollingRadiiTyre>();
  catalog.forEach((tyre) => {
    tyreById.set(tyre.tyre_id, tyre);
    tyre.elements.forEach((el) => {
      elementByRunId.set(String(el.id), { element: el, tyre });
    });
  });

  const tyreLabel = (tyre: RollingRadiiTyre): string => {
    const parts = [tyre.manufacturer, tyre.tyre_name].filter(Boolean);
    return parts.length ? parts.join(' ') : tyre.tyre_id.slice(0, 8);
  };

  // Group rows by (tyre_id, inner_width, pressure). Use string keys so
  // catalog averages (numeric) and element rows (numeric) line up.
  const groups = new Map<string, TyreCatalogRow[]>();
  const groupMeta = new Map<
    string,
    { tyre: RollingRadiiTyre; innerWidth: number; pressure: number }
  >();

  const groupKey = (
    tyreId: string, innerWidth: number, pressure: number,
  ): string => `${tyreId}|${innerWidth}|${pressure}`;

  sessions.forEach((session: any) => {
    const sessionDesc = session?.description || session?.name || 'Unnamed';
    const configs = getValidConfigs(session);
    configs.forEach((config: any) => {
      const runs = Array.isArray(config.runs) ? config.runs : [];
      runs.forEach((run: any) => {
        const match = elementByRunId.get(String(run.id));
        if (!match) return;
        const { element, tyre } = match;
        const key = groupKey(
          tyre.tyre_id,
          element.inner_wheel_width,
          element.tyre_pressure,
        );
        if (!groups.has(key)) {
          groups.set(key, []);
          groupMeta.set(key, {
            tyre,
            innerWidth: element.inner_wheel_width,
            pressure: element.tyre_pressure,
          });
        }
        groups.get(key)!.push({
          kind: 'data',
          tyreLabel: tyreLabel(tyre),
          innerWidth: element.inner_wheel_width,
          pressure: element.tyre_pressure,
          sessionDesc,
          configLetter: numberToLetters(config.letter_number),
          configDesc: config.description || '-',
          runNumber: run.run_number ?? '-',
          circumferenceCalc: element.wheel_circumference_calc,
        });
      });
    });
  });

  // Sort groups by tyre label, then width, then pressure.
  const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
    const ma = groupMeta.get(a)!;
    const mb = groupMeta.get(b)!;
    const la = tyreLabel(ma.tyre);
    const lb = tyreLabel(mb.tyre);
    if (la !== lb) return la.localeCompare(lb);
    if (ma.innerWidth !== mb.innerWidth) {
      return ma.innerWidth - mb.innerWidth;
    }
    return ma.pressure - mb.pressure;
  });

  const rows: TyreCatalogRow[] = [];
  sortedKeys.forEach((key) => {
    const meta = groupMeta.get(key)!;
    const groupRows = groups.get(key)!;
    // Sort rows within the group for consistent display.
    groupRows.sort((a, b) => {
      if (a.sessionDesc !== b.sessionDesc) {
        return a.sessionDesc.localeCompare(b.sessionDesc);
      }
      if (a.configLetter !== b.configLetter) {
        return a.configLetter.localeCompare(b.configLetter);
      }
      const rn = (v: any) => (typeof v === 'number' ? v : 0);
      return rn(a.runNumber) - rn(b.runNumber);
    });
    rows.push(...groupRows);

    // Append the catalog-average row for this (tyre, width, pressure).
    const catalogEntry = meta.tyre.averages.find(
      (a) => a.inner_wheel_width === meta.innerWidth
        && a.tyre_pressure === meta.pressure,
    );
    rows.push({
      kind: 'average',
      tyreLabel: tyreLabel(meta.tyre),
      innerWidth: meta.innerWidth,
      pressure: meta.pressure,
      sessionDesc: '',
      configLetter: '',
      configDesc: 'Catalog average',
      runNumber: '',
      circumferenceCalc: catalogEntry ? catalogEntry.average : null,
      nSamples: catalogEntry ? catalogEntry.n : 0,
    });
  });

  return rows;
}

function ViewParamPair({
  config,
  refConfig,
  refConfigs,
  refId,
  stripe,
  onRefChange,
}: {
  config: any;
  refConfig: any;
  refConfigs: any[];
  refId: string;
  stripe: string;
  onRefChange: (refId: string) => void;
}): ReactElement {
  const letter = numberToLetters(
    config.letter_number,
  );
  return (
    <>
      {/* Row 1: Viewed session values */}
      <tr
        className={
          'cr-border-b cr-border-gray-100 ' + stripe
        }
      >
        <td
          className={
            'cr-px-3 cr-py-1.5 cr-font-semibold'
          }
        >
          {letter}
        </td>
        <td className="cr-px-3 cr-py-1.5">
          {config.description || '-'}
        </td>
        {PARAM_FIELDS.map((f) => (
          <td key={f} className="cr-px-3 cr-py-1.5">
            {formatVal(config[f])}
          </td>
        ))}
      </tr>
      {/* Row 2: Reference session values */}
      <tr
        className={
          'cr-border-b-2 cr-border-gray-300 '
          + stripe
        }
      >
        <td className="cr-px-3 cr-py-1.5">
          <select
            value={refId ?? ''}
            onChange={(e) =>
              onRefChange(e.target.value)
            }
            className={
              'cr-w-16 cr-p-1 cr-text-sm '
              + 'cr-border cr-rounded '
              + 'cr-border-blue-400 cr-bg-blue-50'
            }
          >
            {refConfigs.map((rc2: any) => (
              <option key={rc2.id} value={rc2.id}>
                {numberToLetters(rc2.letter_number)}
              </option>
            ))}
          </select>
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5 cr-text-xs '
            + 'cr-italic cr-text-blue-500'
          }
        >
          {refConfig?.description || '-'}
        </td>
        {PARAM_FIELDS.map((f) => (
          <td
            key={f}
            className={
              'cr-px-3 cr-py-1.5 cr-text-blue-500'
            }
          >
            {formatVal(refConfig?.[f])}
          </td>
        ))}
      </tr>
    </>
  );
}

export default function ParametersCoordination({
  sessions,
  selectedSessionId,
}: ParametersCoordinationProps): ReactElement {
  const queryClient = useQueryClient();
  const wheelAndReprocess = useSetWheelAndReprocessDynamic();
  const [setCircProgress, setSetCircProgress] = useState<{
    running: boolean;
    current: number;
    total: number;
  }>({ running: false, current: 0, total: 0 });

  const [viewSessionId, setViewSessionId] = useState(
    sessions[1]?.id ?? sessions[0]?.id ?? '',
  );

  const refSession = sessions[0];
  const refConfigs = useMemo(
    () => getValidConfigs(refSession),
    [refSession],
  );

  const viewSession = useMemo(
    () => sessions.find((s) => s.id === viewSessionId),
    [sessions, viewSessionId],
  );

  const viewConfigs = useMemo(
    () => getValidConfigs(viewSession),
    [viewSession],
  );

  // Stable list of session IDs (ref first, view second, deduped) feeding the
  // scoped catalog query so its per-request cost is bounded to ~2 sessions.
  const catalogSessionIds = useMemo(() => {
    const ids: string[] = [];
    if (refSession?.id) ids.push(refSession.id);
    if (viewSession?.id && viewSession.id !== refSession?.id) {
      ids.push(viewSession.id);
    }
    return ids;
  }, [refSession?.id, viewSession?.id]);

  const {
    data: rollingRadiiCatalog,
    isLoading: catalogLoading,
  } = useRollingRadiiElements(catalogSessionIds);

  const [refMapping, setRefMapping] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const m: Record<string, string> = {};
    viewConfigs.forEach((vc: any, i: number) => {
      const rc = refConfigs[i] ?? refConfigs[0];
      if (rc) m[vc.id] = rc.id;
    });
    setRefMapping(m);
  }, [viewSessionId]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'session-tyre-reference',
        selectedSessionId,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: ['rolling-radii-elements'],
    });
  };

  // Build flat catalog table — pair both viewed + reference sessions against
  // the cached rolling-radii elements, then group by (tyre, width, pressure).
  const catalogRows = useMemo(() => {
    const sessionSet: any[] = [];
    if (refSession) sessionSet.push(refSession);
    if (viewSession && viewSession !== refSession) {
      sessionSet.push(viewSession);
    }
    return buildTyreCatalogRows(sessionSet, rollingRadiiCatalog);
  }, [refSession, viewSession, rollingRadiiCatalog]);

  const handleSetCircumference = async () => {
    // Run sequentially across every session in the composite view. The new
    // per-session rolling-radii store makes each call cheap, so iterating is
    // practical. Sequential (not parallel) keeps backend load predictable and
    // surfaces per-session failures cleanly in the toast message.
    const targets = sessions.filter((s: any) => s?.id);
    if (targets.length === 0) {
      toast.error('No sessions to process');
      return;
    }
    setSetCircProgress({
      running: true, current: 0, total: targets.length,
    });
    const failed: string[] = [];
    for (let i = 0; i < targets.length; i += 1) {
      const s = targets[i];
      setSetCircProgress({
        running: true, current: i + 1, total: targets.length,
      });
      try {
        await wheelAndReprocess.mutateAsync(s.id);
      } catch (err) {
        console.error(
          `Set circumference error for session ${s.id}:`, err,
        );
        failed.push(sessionLabel(s));
      }
    }
    setSetCircProgress({
      running: false, current: 0, total: 0,
    });
    invalidate();
    if (failed.length === 0) {
      toast.success(
        `Wheel circumference set for ${targets.length} session`
        + `${targets.length === 1 ? '' : 's'}`,
      );
    } else if (failed.length < targets.length) {
      toast.error(
        `${targets.length - failed.length}/${targets.length} sessions `
        + `succeeded. Failed: ${failed.join(', ')}`,
      );
    } else {
      toast.error('Failed to set wheel circumference for any session');
    }
  };

  const selectCls =
    'cr-w-full cr-p-2 cr-text-sm cr-text-blue-900 '
    + 'cr-border cr-border-gray-300 cr-rounded-md '
    + 'focus:cr-ring-2 focus:cr-ring-blue-500 '
    + 'focus:cr-border-blue-500';

  const btnCls =
    'cr-px-3 cr-py-1.5 cr-text-sm '
    + '!cr-normal-case !cr-font-bold '
    + '!cr-bg-gray-100 !cr-text-blue-900 '
    + '!cr-border-gray-300';

  return (
    <div className="cr-space-y-4">
      {/* Session to View */}
      <div className="md:cr-w-1/2">
        <label
          className={
            'cr-block cr-mb-1 cr-text-sm '
            + 'cr-font-medium cr-text-blue-900'
          }
        >
          Session to View
        </label>
        <select
          value={viewSessionId}
          onChange={(e) =>
            setViewSessionId(e.target.value)
          }
          className={selectCls}
        >
          {sessions.map((s: any) => (
            <option key={s.id} value={s.id}>
              {sessionLabel(s)}
            </option>
          ))}
        </select>
      </div>

      {/* Parameters Table */}
      {viewConfigs.length > 0 && (
        <div className="cr-overflow-x-auto">
          <table
            className={
              'cr-w-full cr-text-sm cr-text-left '
              + 'cr-text-blue-900'
            }
          >
            <thead>
              <tr
                className={
                  'cr-bg-cirrusBlue-50 cr-text-xs '
                  + 'cr-uppercase'
                }
              >
                <th className="cr-px-3 cr-py-2">
                  Cfg
                </th>
                <th className="cr-px-3 cr-py-2">
                  Description
                </th>
                {PARAM_HEADERS.map((h) => (
                  <th
                    key={h}
                    className="cr-px-3 cr-py-2"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewConfigs.map(
                (config: any, idx: number) => {
                  const refId =
                    refMapping[config.id];
                  const rc = refConfigs.find(
                    (c: any) => c.id === refId,
                  );
                  const stripe = idx % 2
                    ? 'cr-bg-gray-50'
                    : 'cr-bg-white';
                  return (
                    <ViewParamPair
                      key={config.id}
                      config={config}
                      refConfig={rc}
                      refConfigs={refConfigs}
                      refId={refId}
                      stripe={stripe}
                      onRefChange={(rid) =>
                        setRefMapping((p) => ({
                          ...p,
                          [config.id]: rid,
                        }))
                      }
                    />
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Action Buttons */}
      <div
        className={
          'cr-flex cr-flex-wrap cr-gap-2 cr-pt-2'
        }
      >
        <Button
          onClick={handleSetCircumference}
          disabled={setCircProgress.running}
          icon={
            <HiOutlinePlayCircle
              className="cr-size-5"
            />
          }
          className={btnCls}
        >
          {setCircProgress.running
            ? `Processing ${setCircProgress.current}/${setCircProgress.total}...`
            : `Set Circumference (${sessions.length} session${sessions.length === 1 ? '' : 's'})`}
        </Button>
      </div>

      {/* Tyre Catalog Table — populated from rolling_radii_elements */}
      <div className="cr-pt-2">
        <h3
          className={
            'cr-text-sm cr-font-semibold cr-text-blue-900 '
            + 'cr-mb-2'
          }
        >
          Tyre Catalog (speed-sensor wheel)
        </h3>
        {catalogLoading && (
          <div className="cr-text-sm cr-text-gray-500">
            Loading tyre catalog…
          </div>
        )}
        {!catalogLoading && catalogRows.length === 0 && (
          <div className="cr-text-sm cr-text-gray-500">
            No catalog entries match the runs in these sessions.
            Press <b>Set Circumference</b> to populate the catalog.
          </div>
        )}
        {!catalogLoading && catalogRows.length > 0 && (
          <div className="cr-overflow-x-auto">
            <table
              className={
                'cr-w-full cr-text-sm cr-text-left '
                + 'cr-text-blue-900'
              }
            >
              <thead>
                <tr
                  className={
                    'cr-bg-cirrusBlue-50 cr-text-xs '
                    + 'cr-uppercase'
                  }
                >
                  <th className="cr-px-3 cr-py-2">Tyre</th>
                  <th className="cr-px-3 cr-py-2">
                    Rim Inner (mm)
                  </th>
                  <th className="cr-px-3 cr-py-2">
                    Pressure (bar)
                  </th>
                  <th className="cr-px-3 cr-py-2">Session</th>
                  <th className="cr-px-3 cr-py-2">Cfg</th>
                  <th className="cr-px-3 cr-py-2">
                    Config Desc.
                  </th>
                  <th className="cr-px-3 cr-py-2">Run</th>
                  <th className="cr-px-3 cr-py-2">
                    Circ. Calc (m)
                  </th>
                </tr>
              </thead>
              <tbody>
                {catalogRows.map((row, idx) => {
                  const isAvg = row.kind === 'average';
                  const baseCls = isAvg
                    ? 'cr-bg-cirrusBlue-50 cr-font-semibold '
                      + 'cr-border-b-2 cr-border-gray-300'
                    : (idx % 2
                      ? 'cr-bg-gray-50'
                      : 'cr-bg-white')
                      + ' cr-border-b cr-border-gray-100';
                  return (
                    <tr
                      key={`${row.tyreLabel}-${row.innerWidth}-`
                        + `${row.pressure}-${idx}`}
                      className={baseCls}
                    >
                      <td className="cr-px-3 cr-py-1.5">
                        {row.tyreLabel}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {row.innerWidth}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {row.pressure}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {row.sessionDesc}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {row.configLetter}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {isAvg
                          ? `Catalog average (n=${row.nSamples ?? 0})`
                          : row.configDesc}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {row.runNumber}
                      </td>
                      <td className="cr-px-3 cr-py-1.5">
                        {formatVal(row.circumferenceCalc)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
